import type {
  CampaignCreationCapabilities,
  SmartCreationObjectiveRecommendation,
  SmartCreationRecommendationEvidence,
} from "@adpilot/shared";
import { IconInfoCircle } from "@tabler/icons-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, XAxis } from "recharts";
import { Button } from "@/components/ui/button";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatCurrency } from "./objectives-model";

interface MetricPoint {
  date: string;
  value: number;
}

const METRIC_COLORS = {
  acos: "#2563eb",
  orders: "#7c3aed",
  spend: "#d97706",
} as const;

function MetricAreaChart({
  label,
  points,
  color,
}: {
  label: string;
  points: MetricPoint[];
  color: string;
}) {
  const gradientId = `metric-${useId().replace(/:/g, "")}`;
  const config = { value: { label, color } } satisfies ChartConfig;
  if (points.length === 0) return <div className="h-[72px] min-w-0 flex-1" />;

  return (
    <ChartContainer
      config={config}
      className="h-[72px] min-w-0 flex-1 aspect-auto"
      initialDimension={{ width: 220, height: 72 }}
    >
      <AreaChart data={points} margin={{ top: 7, right: 2, bottom: 0, left: 2 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.42} />
            <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={5}
          minTickGap={24}
          tickFormatter={(value: string) => value.slice(5)}
          fontSize={9}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              indicator="dot"
              labelFormatter={(_value, payload) => payload[0]?.payload?.date ?? ""}
            />
          }
        />
        <Area
          dataKey="value"
          type="natural"
          fill={`url(#${gradientId})`}
          stroke="var(--color-value)"
          strokeWidth={1.75}
        />
      </AreaChart>
    </ChartContainer>
  );
}

export function HistoricalReference({
  capabilities,
  recommendation,
  evidence,
  pending,
  failed,
  onRefresh,
}: {
  capabilities: CampaignCreationCapabilities;
  recommendation: SmartCreationObjectiveRecommendation | null;
  evidence?: SmartCreationRecommendationEvidence;
  pending: boolean;
  failed: boolean;
  onRefresh: () => void;
}) {
  const { t } = useTranslation("smartCreation");
  const history = evidence?.historicalReference;
  const series = history?.series ?? [];
  const all = history?.allSponsoredProducts ?? {
    spend: 0,
    sales: 0,
    orders: 0,
    observedAcos: null,
  };
  const selectedHasHistory = Boolean(
    evidence &&
      (evidence.sampleSize.impressions > 0 ||
        evidence.sampleSize.clicks > 0 ||
        evidence.sampleSize.orders > 0 ||
        evidence.metrics.spend > 0 ||
        evidence.metrics.sales > 0),
  );
  const shopHasHistory = Boolean(all.spend > 0 || all.sales > 0 || all.orders > 0);
  const unresolvedDescription = evidence
    ? null
    : pending
      ? t("objectives.history.loading")
      : t(failed ? "objectives.history.failed" : "objectives.history.noSelectedHistory");
  const metrics = [
    {
      key: "acos",
      selectedValue:
        evidence?.metrics.observedAcos == null
          ? "—"
          : `${(evidence.metrics.observedAcos * 100).toFixed(2)}%`,
      allValue: all.observedAcos == null ? "—" : `${(all.observedAcos * 100).toFixed(2)}%`,
      color: METRIC_COLORS.acos,
      selectedPoints: series.map((point) => ({
        date: point.date,
        value:
          point.selectedProducts.sales > 0
            ? point.selectedProducts.spend / point.selectedProducts.sales
            : 0,
      })),
      allPoints: series.map((point) => ({
        date: point.date,
        value:
          point.allSponsoredProducts.sales > 0
            ? point.allSponsoredProducts.spend / point.allSponsoredProducts.sales
            : 0,
      })),
    },
    {
      key: "orders",
      color: METRIC_COLORS.orders,
      selectedValue: new Intl.NumberFormat().format(evidence?.sampleSize.orders ?? 0),
      allValue: new Intl.NumberFormat().format(all.orders),
      selectedPoints: series.map((point) => ({
        date: point.date,
        value: point.selectedProducts.orders,
      })),
      allPoints: series.map((point) => ({
        date: point.date,
        value: point.allSponsoredProducts.orders,
      })),
    },
    {
      key: "spend",
      color: METRIC_COLORS.spend,
      selectedValue: formatCurrency(
        evidence?.metrics.spend ?? 0,
        capabilities.currencyCode,
        capabilities.dailyBudget.decimals,
      ),
      allValue: formatCurrency(
        all.spend,
        capabilities.currencyCode,
        capabilities.dailyBudget.decimals,
      ),
      selectedPoints: series.map((point) => ({
        date: point.date,
        value: point.selectedProducts.spend,
      })),
      allPoints: series.map((point) => ({
        date: point.date,
        value: point.allSponsoredProducts.spend,
      })),
    },
  ];

  return (
    <section className="flex flex-col gap-2.5" aria-labelledby="historical-reference-title">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1.5">
          <h3 id="historical-reference-title" className="text-sm font-semibold">
            {t("objectives.history.title")}
          </h3>
          <EvidencePopover
            recommendation={recommendation}
            evidence={evidence}
            pending={pending}
            onRefresh={onRefresh}
          />
          {failed ? (
            <Button variant="outline" size="xs" onClick={onRefresh}>
              {t("actions.retry")}
            </Button>
          ) : null}
        </div>
        <span className="text-xs text-muted-foreground">
          {t("objectives.history.window", {
            days: evidence?.actualWindowDays ?? 14,
            date: evidence?.dataThrough ?? "—",
          })}
        </span>
      </div>
      <HistoryRow
        title={t("objectives.history.selectedProducts")}
        emptyDescription={
          unresolvedDescription ??
          (!selectedHasHistory ? t("objectives.history.noSelectedHistory") : undefined)
        }
        metrics={metrics.map((metric) => ({
          label: t(`objectives.history.metrics.${metric.key}`),
          value: metric.selectedValue,
          points: metric.selectedPoints,
          color: metric.color,
        }))}
      />
      <HistoryRow
        title={t("objectives.history.allCampaigns")}
        emptyDescription={
          unresolvedDescription ??
          (!shopHasHistory ? t("objectives.history.noShopHistory") : undefined)
        }
        metrics={metrics.map((metric) => ({
          label: t(`objectives.history.metrics.${metric.key}`),
          value: metric.allValue,
          points: metric.allPoints,
          color: metric.color,
        }))}
      />
    </section>
  );
}

function HistoryRow({
  title,
  metrics,
  emptyDescription,
}: {
  title: string;
  metrics: Array<{ label: string; value: string; points: MetricPoint[]; color: string }>;
  emptyDescription?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <h4 className="text-xs font-semibold">{title}</h4>
      {emptyDescription ? (
        <div className="flex min-h-24 items-center rounded-md border border-dashed border-line bg-subtle/25 px-4 text-xs text-muted-foreground">
          {emptyDescription}
        </div>
      ) : (
        <div className="grid gap-2.5 md:grid-cols-3">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="flex min-h-24 items-center gap-3 rounded-md border border-line bg-surface px-3 py-2"
            >
              <div className="flex w-28 shrink-0 flex-col gap-0.5">
                <span className="text-[11px] text-muted-foreground">{metric.label}</span>
                <span className="font-mono text-base font-semibold tabular-nums">
                  {metric.value}
                </span>
              </div>
              <MetricAreaChart label={metric.label} points={metric.points} color={metric.color} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EvidencePopover({
  recommendation,
  evidence,
  pending,
  onRefresh,
}: {
  recommendation: SmartCreationObjectiveRecommendation | null;
  evidence?: SmartCreationRecommendationEvidence;
  pending: boolean;
  onRefresh: () => void;
}) {
  const { t } = useTranslation("smartCreation");
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            aria-label={t("objectives.evidence.auditTitle")}
          />
        }
      >
        <IconInfoCircle />
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-80 p-0">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <PopoverTitle className="text-sm leading-5">
              {t("objectives.evidence.auditTitle")}
            </PopoverTitle>
            <PopoverDescription className="text-xs leading-5">
              {recommendation
                ? t(
                    recommendation.mode === "historical"
                      ? "objectives.evidence.historicalExplanation"
                      : "objectives.evidence.fallbackExplanation",
                  )
                : t(
                    evidence
                      ? "objectives.evidence.historyExplanation"
                      : "objectives.history.noSelectedHistory",
                  )}
            </PopoverDescription>
          </div>
          {evidence ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted-foreground">{t("objectives.evidence.dataThrough")}</dt>
              <dd className="text-right font-mono">{evidence.dataThrough ?? "—"}</dd>
              <dt className="text-muted-foreground">{t("objectives.evidence.coverage")}</dt>
              <dd className="text-right font-mono">
                {t("objectives.evidence.coverageValue", {
                  covered: evidence.coverage.coveredDays,
                  expected: evidence.coverage.expectedDays,
                })}
              </dd>
              <dt className="text-muted-foreground">{t("objectives.evidence.sample")}</dt>
              <dd className="text-right font-mono">
                {t("objectives.evidence.sampleCompact", {
                  clicks: evidence.sampleSize.clicks,
                  orders: evidence.sampleSize.orders,
                })}
              </dd>
            </dl>
          ) : null}
          <Button type="button" size="sm" variant="outline" loading={pending} onClick={onRefresh}>
            {t("objectives.actions.refresh")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
